import { useState, type FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import classnames from 'classnames';
import dibsCss from 'dibs-css';
import { defineMessages, useIntl } from 'dibs-react-intl';
import { Link } from 'dibs-elements/exports/Link.tsx';
import { ClientSuspense } from 'dibs-elements/exports/ClientSuspense.tsx';
import { dibsLazyClient } from 'dibs-lazy/exports/dibsLazyClient.ts';
import { PdpSharedRingSizeGuideModalLazy } from '../shared/PdpSharedDetail/PdpSharedRingSizeGuideModalLazy.tsx';
import { ringSize } from '../messages/pdpSwatchSelectorMessages.tsx';

import styles from '../styles/PdpSharedSwatchSelector.module.css';

import {
    type PdpRespSwatchSelector_item$data,
    type PdpRespSwatchSelector_item$key,
} from './__generated__/PdpRespSwatchSelector_item.graphql.ts';
import { type PdpRespSwatchSelector_user$key } from './__generated__/PdpRespSwatchSelector_user.graphql.ts';

type SwatchSelectorProps = {
    currency?: string;
    selectedSkuId?: string | null;
    item: PdpRespSwatchSelector_item$data;
    userIsTrade: boolean;
    transactions: null;
    onClick?: (skuId: string) => void;
    pageLayout: 'PDP';
    showSwatchDescriptionOnHover?: boolean;
    inputSize?: 'small';
};

const SwatchSelector = dibsLazyClient<FC<SwatchSelectorProps>>(
    () => import(/* webpackChunkName: "swatchSelectorMain" */ './SwatchSelectorMain.tsx')
);

const messages = defineMessages({
    sizeGuide: {
        id: 'pdp.respSwatchSelectorRingSizeGuide',
        defaultMessage: 'Size Guide',
    },
});

const itemFragment = graphql`
    fragment PdpRespSwatchSelector_item on Item {
        ...SwatchSelector_item @arguments(shouldFetchBuyerInfo: true)
        isRingItem
    }
`;

const userFragment = graphql`
    fragment PdpRespSwatchSelector_user on User {
        isVerifiedTrade
    }
`;

type Props = {
    isClient?: boolean | null;
    currency?: string;
    selectedSkuId?: string | null;
    onClick?: (skuId: string) => void;
    showSwatchDescriptionOnHover?: boolean;
    user?: PdpRespSwatchSelector_user$key | null;
    item: PdpRespSwatchSelector_item$key;
};

export const PdpRespSwatchSelector: FC<Props> = ({
    currency,
    onClick,
    isClient,
    item: itemRef,
    user: userRef,
    selectedSkuId,
    showSwatchDescriptionOnHover,
}) => {
    const item = useFragment(itemFragment, itemRef);
    const user = useFragment(userFragment, userRef);
    const { isRingItem } = item || {};

    const [modalOpen, setModalOpen] = useState(false);
    const intl = useIntl();

    const swatchSelector = (
        <SwatchSelector
            currency={currency}
            selectedSkuId={selectedSkuId}
            item={item}
            userIsTrade={!!user?.isVerifiedTrade}
            transactions={null}
            onClick={onClick}
            pageLayout="PDP"
            showSwatchDescriptionOnHover={showSwatchDescriptionOnHover}
            inputSize={isRingItem ? 'small' : undefined}
        />
    );

    return (
        <ClientSuspense fallback={''}>
            {isRingItem ? (
                <div className={classnames(dibsCss.flex, dibsCss.justifyBetween, dibsCss.mtSmall)}>
                    <div className={classnames(dibsCss.flex, dibsCss.itemsCenter)}>
                        <div
                            className={classnames(
                                dibsCss.sassyFontBodyMediumHeavy,
                                dibsCss.mrSmall
                            )}
                        >
                            {ringSize}
                        </div>
                        <div
                            className={classnames(
                                dibsCss.sassyFontBodySizeMedium,
                                styles.selectionWidth
                            )}
                        >
                            {swatchSelector}
                        </div>
                    </div>
                    <Link
                        className={dibsCss.sassyFontBodySizeSmall}
                        onClick={() => setModalOpen(true)}
                    >
                        {intl.formatMessage(messages.sizeGuide)}
                    </Link>
                    {isClient && (
                        <PdpSharedRingSizeGuideModalLazy
                            isOpen={modalOpen}
                            onClose={() => setModalOpen(false)}
                        />
                    )}
                </div>
            ) : (
                swatchSelector
            )}
        </ClientSuspense>
    );
};
